/**
 * Module-level cache for Clerk JWT token prefetching.
 *
 * OrganizationGuard starts the getToken() call as soon as it resolves
 * (the user has a valid org membership). ClientProvider then consumes
 * the already-in-flight promise instead of making a fresh getToken()
 * network call — shaving ~200-500ms off the init waterfall.
 */

type GetTokenFn = (opts: { template: string; organizationId?: string }) => Promise<string | null>;

let _prefetchPromise: Promise<string | null> | null = null;
let _prefetchOrgId: string | undefined = undefined;

/**
 * Start prefetching a Clerk JWT token. Idempotent — only the first
 * call actually starts the fetch. Subsequent calls are no-ops.
 */
export function startTokenPrefetch(getToken: GetTokenFn, organizationId?: string): void {
  if (_prefetchPromise) return;
  _prefetchOrgId = organizationId;
  _prefetchPromise = getToken({
    template: 'backendJWT',
    organizationId,
  });
}

/**
 * Consume the prefetched token promise. Returns null if no prefetch
 * was started or if the org ID doesn't match (stale prefetch).
 * Clears the cache after consumption.
 */
export function consumePrefetchedToken(organizationId?: string): Promise<string | null> | null {
  if (!_prefetchPromise) return null;
  if (_prefetchOrgId !== organizationId) {
    _prefetchPromise = null;
    _prefetchOrgId = undefined;
    return null;
  }
  const p = _prefetchPromise;
  _prefetchPromise = null;
  _prefetchOrgId = undefined;
  return p;
}

/** Reset the cache (e.g., before a page reload on org switch) */
export function clearTokenPrefetch(): void {
  _prefetchPromise = null;
  _prefetchOrgId = undefined;
}
